import { motion, useReducedMotion } from "framer-motion";
import { Star, ThumbsUp, Users } from "lucide-react";
import { CountUp } from "@/features/dashboard/components/CountUp";
import type { SentimentSplit } from "../mockAnalytics";

interface SentimentSplitCardProps {
  data: SentimentSplit;
}

const SEGMENTS: { key: keyof SentimentSplit; label: string; color: string; badgeColor: string }[] = [
  {
    key: "positive",
    label: "Positive",
    color: "bg-emerald-500",
    badgeColor: "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200/70 dark:border-emerald-800/60",
  },
  {
    key: "neutral",
    label: "Neutral",
    color: "bg-slate-400 dark:bg-slate-600",
    badgeColor: "text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700",
  },
  {
    key: "negative",
    label: "Negative",
    color: "bg-rose-500",
    badgeColor: "text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border-rose-200/70 dark:border-rose-800/60",
  },
];

/** CSAT score with star rating, plus a stacked positive/neutral/negative sentiment bar. */
export function SentimentSplitCard({ data }: SentimentSplitCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const fullStars = Math.round(data.csatScore);

  return (
    <div className="flex flex-col justify-between h-full space-y-4">
      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight tabular-nums text-slate-900 dark:text-white">
              <CountUp value={data.csatScore} decimals={1} suffix="/5" />
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200/80 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
              <ThumbsUp className="h-3 w-3" />
              High CSAT
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={
                  star <= fullStars
                    ? "h-4 w-4 fill-amber-400 text-amber-400"
                    : "h-4 w-4 fill-slate-200 text-slate-200 dark:fill-slate-800 dark:text-slate-800"
                }
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-850/60 px-3 py-2 text-right">
          <Users className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          <div>
            <div className="text-xs font-bold tabular-nums text-slate-900 dark:text-white">
              {data.totalRatings.toLocaleString()}
            </div>
            <div className="text-[10px] font-medium text-slate-500 dark:text-slate-400">Total Reviews</div>
          </div>
        </div>
      </div>

      {/* Stacked Sentiment Bar */}
      <div className="space-y-3">
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800 p-0.5 gap-0.5">
          {SEGMENTS.map((seg) => {
            const value = data[seg.key] as number;
            return (
              <motion.div
                key={seg.key}
                className={`h-full rounded-full ${seg.color}`}
                initial={{ width: prefersReducedMotion ? `${value}%` : 0 }}
                animate={{ width: `${value}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
            );
          })}
        </div>

        {/* 3 Metric Cards for Sentiment Split */}
        <div className="grid grid-cols-3 gap-2">
          {SEGMENTS.map((seg) => (
            <div
              key={seg.key}
              className="flex flex-col items-center justify-center rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-850/40 p-2 text-center"
            >
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                <span className={`h-2 w-2 rounded-full ${seg.color}`} />
                {seg.label}
              </div>
              <span className="mt-0.5 text-base font-bold tabular-nums text-slate-900 dark:text-white">
                {data[seg.key] as number}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
